// src/pages/SettingsInvoicesStatements.js
import React, { useMemo, useState } from "react";
import "../styles/SettingsInvoicesStatements.css";
import { useTranslation } from "react-i18next";

export default function SettingsInvoicesStatements() {
  // 🔑 Pede o namespace do ficheiro: settings_invoices_statements.json
  const { t } = useTranslation("settings_invoices_statements");

  const [tab, setTab] = useState("invoices"); // invoices | statements | credit-notes
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [drawerId, setDrawerId] = useState(null);

  // --- Mock data (wire to API later) ---
  const invoices = [
    {
      id: "INV-2025-00031",
      issuedAt: "2025-07-27",
      dueAt: "2025-08-27",
      period: "Jul 2025",
      amount: 9900,
      currency: "EUR",
      status: "open", // open | paid | past_due | void
      paymentMethod: "bank_transfer",
      client: "ACME Logistics",
      vatId: "PT123456789",
    },
    {
      id: "INV-2025-00030",
      issuedAt: "2025-06-27",
      dueAt: "2025-07-27",
      period: "Jun 2025",
      amount: 9900,
      currency: "EUR",
      status: "paid",
      paymentMethod: "card",
      client: "ACME Logistics",
      vatId: "PT123456789",
    },
    {
      id: "INV-2025-00029",
      issuedAt: "2025-05-27",
      dueAt: "2025-06-27",
      period: "May 2025",
      amount: 14900,
      currency: "EUR",
      status: "past_due",
      paymentMethod: "bank_transfer",
      client: "RoadFox Transport",
      vatId: "PT987654321",
    },
  ];

  const formatMoney = (cents, cur = "EUR") =>
    new Intl.NumberFormat(undefined, { style: "currency", currency: cur }).format(cents / 100);

  const outstanding = invoices
    .filter((i) => i.status === "open" || i.status === "past_due")
    .reduce((s, i) => s + i.amount, 0);

  const nextInvoice = invoices.find((i) => i.status === "open");
  const showBankBanner = invoices.some(
    (i) => (i.status === "open" || i.status === "past_due") && i.paymentMethod === "bank_transfer"
  );

  const filtered = useMemo(() => {
    return invoices.filter((i) => {
      const q = query.trim().toLowerCase();
      const matchesQ =
        !q || i.id.toLowerCase().includes(q) || i.client.toLowerCase().includes(q);
      const matchesStatus = status === "all" ? true : i.status === status;
      const afterFrom = from ? i.issuedAt >= from : true;
      const beforeTo = to ? i.issuedAt <= to : true;
      return matchesQ && matchesStatus && afterFrom && beforeTo;
    });
  }, [invoices, query, status, from, to]);

  return (
    <div className="settings-content">
      <div className="sis-head">
        <h1>{t("title")}</h1>
        <div className="sis-head__actions">
          <button className="sis-btn sis-btn-secondary" onClick={() => alert(t("actions.export_csv_ellipsis"))}>
            {t("actions.export_csv")}
          </button>
          <button className="sis-btn sis-btn-primary" onClick={() => alert(t("actions.download_statement_ellipsis"))}>
            {t("actions.download_statement")}
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="sis-kpis">
        <div className="sis-kpi">
          <div className="sis-kpi__label">{t("kpis.outstanding")}</div>
          <div className="sis-kpi__value">{formatMoney(outstanding)}</div>
        </div>
        <div className="sis-kpi">
          <div className="sis-kpi__label">{t("kpis.next_invoice")}</div>
          <div className="sis-kpi__value">
            {nextInvoice ? `${formatMoney(nextInvoice.amount)} • ${nextInvoice.dueAt}` : "—"}
          </div>
        </div>
        <div className="sis-kpi">
          <div className="sis-kpi__label">{t("kpis.last_payment")}</div>
          <div className="sis-kpi__value">2025-07-02 • {formatMoney(9900)}</div>
        </div>
      </div>

      {/* Awaiting bank transfer banner */}
      {showBankBanner && (
        <div className="sis-bank">
          <div>
            <strong>{t("bank_banner.title")}</strong>. {t("bank_banner.subtitle")}
          </div>
          <div className="sis-bank__grid">
            <div><span>{t("bank_banner.beneficiary_label")}</span><b>LOADEvo, Lda</b></div>
            <div><span>{t("bank_banner.iban_label")}</span><b>PT50 0035 0001 0000 1234 5 06</b></div>
            <div><span>{t("bank_banner.swift_label")}</span><b>CGDIPTPL</b></div>
            <div><span>{t("bank_banner.reference_label")}</span><b>{t("bank_banner.reference_example")}</b></div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="sis-tabs">
        <button className={`sis-tab ${tab === "invoices" ? "active" : ""}`} onClick={() => setTab("invoices")}>
          {t("tabs.invoices")}
        </button>
        <button className={`sis-tab ${tab === "statements" ? "active" : ""}`} onClick={() => setTab("statements")}>
          {t("tabs.statements")}
        </button>
        <button className={`sis-tab ${tab === "credit-notes" ? "active" : ""}`} onClick={() => setTab("credit-notes")}>
          {t("tabs.credit_notes")}
        </button>
      </div>

      {/* Invoices */}
      {tab === "invoices" && (
        <>
          <div className="sis-filters">
            <input
              className="sis-input"
              placeholder={t("filters.search_placeholder")}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <select className="sis-input" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="all">{t("filters.status_all")}</option>
              <option value="open">{t("statuses.open")}</option>
              <option value="past_due">{t("statuses.past_due")}</option>
              <option value="paid">{t("statuses.paid")}</option>
              <option value="void">{t("statuses.void")}</option>
            </select>
            <input className="sis-input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            <input className="sis-input" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            <button
              className="sis-btn sis-btn-tertiary"
              onClick={() => { setQuery(""); setStatus("all"); setFrom(""); setTo(""); }}
            >
              {t("filters.reset")}
            </button>
          </div>

          <div className="sis-table">
            <div className="sis-th">
              <div>{t("table.invoice")}</div>
              <div>{t("table.client")}</div>
              <div>{t("table.issued")}</div>
              <div>{t("table.due")}</div>
              <div>{t("table.amount")}</div>
              <div>{t("table.status")}</div>
              <div className="right">{t("table.actions")}</div>
            </div>

            {filtered.map((inv) => (
              <div className="sis-tr" key={inv.id} onClick={() => setDrawerId(inv.id)}>
                <div className="mono">{inv.id}</div>
                <div>{inv.client}</div>
                <div>{inv.issuedAt}</div>
                <div>{inv.dueAt}</div>
                <div>{formatMoney(inv.amount, inv.currency)}</div>
                <div><span className={`sis-pill sis-${inv.status}`}>{t(`statuses.${inv.status}`)}</span></div>
                <div className="right">
                  <button
                    className="sis-btn sis-btn-ghost"
                    onClick={(e) => { e.stopPropagation(); alert(t("actions.download_pdf_ellipsis")); }}
                  >
                    {t("actions.pdf")}
                  </button>
                  {inv.status !== "paid" && (
                    <button
                      className="sis-btn sis-btn-primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        inv.paymentMethod === "bank_transfer"
                          ? alert(t("actions.show_bank_transfer_modal_ellipsis"))
                          : alert(t("actions.open_card_payment_flow_ellipsis"));
                      }}
                    >
                      {t("actions.pay")}
                    </button>
                  )}
                </div>
              </div>
            ))}

            {!filtered.length && <div className="sis-empty">{t("empty.invoices")}</div>}
          </div>
        </>
      )}

      {/* Statements */}
      {tab === "statements" && (
        <div className="sis-card">
          <h3>{t("statements.title")}</h3>
          <p>{t("statements.subtitle")}</p>
          <div className="sis-grid">
            <div>
              <label>{t("form.from")}</label>
              <input className="sis-input" type="date" />
            </div>
            <div>
              <label>{t("form.to")}</label>
              <input className="sis-input" type="date" />
            </div>
            <div>
              <label>{t("form.client_optional")}</label>
              <input className="sis-input" placeholder="ACME Logistics…" />
            </div>
            <div className="sis-actions">
              <button className="sis-btn sis-btn-secondary" onClick={() => alert(t("actions.preview_statement_ellipsis"))}>
                {t("actions.preview")}
              </button>
              <button className="sis-btn sis-btn-primary" onClick={() => alert(t("actions.download_statement_pdf_ellipsis"))}>
                {t("actions.download_pdf")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Credit Notes */}
      {tab === "credit-notes" && <div className="sis-empty">{t("empty.credit_notes")}</div>}

      {/* Drawer */}
      {drawerId && (
        <div className="sis-drawer" onClick={() => setDrawerId(null)}>
          <div className="sis-drawer__panel" onClick={(e) => e.stopPropagation()}>
            {(() => {
              const inv = invoices.find((x) => x.id === drawerId);
              if (!inv) return null;
              return (
                <>
                  <div className="sis-drawer__head">
                    <h3>{inv.id}</h3>
                    <span className={`sis-pill sis-${inv.status}`}>{t(`statuses.${inv.status}`)}</span>
                  </div>

                  <div className="sis-meta">
                    <div><span>{t("drawer.client")}</span><b>{inv.client}</b></div>
                    <div><span>{t("drawer.issued")}</span><b>{inv.issuedAt}</b></div>
                    <div><span>{t("drawer.due")}</span><b>{inv.dueAt}</b></div>
                    <div><span>{t("drawer.period")}</span><b>{inv.period}</b></div>
                    <div><span>{t("drawer.vat")}</span><b>{inv.vatId}</b></div>
                    <div><span>{t("drawer.total")}</span><b>{formatMoney(inv.amount, inv.currency)}</b></div>
                  </div>

                  <div className="sis-section">
                    <h4>{t("drawer.line_items")}</h4>
                    <div className="sis-lines">
                      <div className="sis-line">
                        <div>{t("drawer.fleet_plan", { period: inv.period })}</div>
                        <div className="right">{formatMoney(inv.amount)}</div>
                      </div>
                    </div>
                  </div>

                  <div className="sis-drawer__actions">
                    <button className="sis-btn sis-btn-secondary" onClick={() => alert(t("actions.open_pdf_ellipsis"))}>
                      {t("actions.view_pdf")}
                    </button>
                    <button className="sis-btn sis-btn-ghost" onClick={() => alert(t("actions.email_invoice_ellipsis"))}>
                      {t("actions.email")}
                    </button>
                    {inv.status !== "paid" && (
                      <button
                        className="sis-btn sis-btn-primary"
                        onClick={() =>
                          inv.paymentMethod === "bank_transfer"
                            ? alert(t("actions.show_bank_transfer_modal_ellipsis"))
                            : alert(t("actions.open_card_payment_flow_ellipsis"))
                        }
                      >
                        {t("actions.pay_now")}
                      </button>
                    )}
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
